/**
 * CR500 Phase 2B: ValidatorBlock
 * 
 * Renders validator/verification results in deal details.
 * Only shown for admin users or when validation data is present.
 */

import React from "react";
import { ShieldCheck, ShieldAlert, ShieldQuestion } from "lucide-react";
import type { DealCardBlockProps } from "@/lib/deal-cards/specV2/types";
import type { Deal } from "@/lib/types/deals";
import { cn } from "@/lib/utils";

type ValidationStatus = "verified" | "unverified" | "warning" | "unknown";

export const ValidatorBlock: React.FC<DealCardBlockProps<Deal>> = ({
  deal,
  block,
  context,
}) => {
  // Don't render if block is disabled
  if (!block.enabled) {
    return null;
  }

  // Only show for admins
  if (!context.isAdmin) {
    return null;
  }

  // Default status for now (deal doesn't have validation_status field yet)
  const status: ValidationStatus = "unknown";
  const validationNotes = deal.valuation_notes || null;
  
  // Status styling and icons
  const statusConfig: Record<ValidationStatus, {
    icon: typeof ShieldCheck;
    label: string;
    className: string;
  }> = {
    verified: {
      icon: ShieldCheck,
      label: "Verified",
      className: "text-emerald-600 bg-emerald-50",
    },
    unverified: {
      icon: ShieldQuestion,
      label: "Unverified",
      className: "text-amber-600 bg-amber-50",
    },
    warning: {
      icon: ShieldAlert,
      label: "Warning",
      className: "text-red-600 bg-red-50",
    },
    unknown: {
      icon: ShieldQuestion,
      label: "Unknown",
      className: "text-muted-foreground bg-muted",
    },
  };

  const config = statusConfig[status] || statusConfig.unknown;
  const Icon = config.icon;

  return (
    <div 
      className="space-y-2"
      data-block="validator"
      data-block-id={block.id}
    >
      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
        {context.labels?.validator_title || "Validation"}
      </h4>
      
      <div className={cn(
        "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium",
        config.className,
      )}>
        <Icon className="w-3.5 h-3.5" />
        {config.label}
      </div>
      
      {/* Validation notes (admin only) */}
      {context.isAdmin && validationNotes && (
        <p className="text-xs text-muted-foreground mt-1 pl-1">
          {validationNotes}
        </p>
      )}
    </div>
  );
};

export default ValidatorBlock;
